import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Divider,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

interface TokenComparisonToolProps {
  onBack: () => void;
}

interface DecodedToken {
  header: any;
  payload: any;
  raw: string;
}

interface ComparisonInsight {
  type: 'info' | 'warning' | 'error' | 'success';
  category: string;
  message: string;
  details?: string;
}

const TokenComparisonTool: React.FC<TokenComparisonToolProps> = ({ onBack }) => {
  const [token1, setToken1] = useState('');
  const [token2, setToken2] = useState('');
  const [decoded1, setDecoded1] = useState<DecodedToken | null>(null);
  const [decoded2, setDecoded2] = useState<DecodedToken | null>(null);
  const [error1, setError1] = useState<string | null>(null);
  const [error2, setError2] = useState<string | null>(null);
  const [comparisonMode, setComparisonMode] = useState<'side-by-side' | 'diff'>('side-by-side');
  const [insights, setInsights] = useState<ComparisonInsight[]>([]);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Decode JWT token with proper UTF-8 handling
  const decodeToken = (token: string): DecodedToken | null => {
    try {
      const parts = token.trim().split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format');
      }

      // Helper to decode base64url with proper UTF-8 support
      const base64UrlDecode = (str: string): string => {
        // Convert base64url to base64
        const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
        // Decode base64 to binary string
        const binaryString = atob(base64);
        // Convert binary string to UTF-8
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        // Decode UTF-8 bytes to string
        return new TextDecoder('utf-8').decode(bytes);
      };

      const header = JSON.parse(base64UrlDecode(parts[0]));
      const payload = JSON.parse(base64UrlDecode(parts[1]));

      return { header, payload, raw: token };
    } catch (err) {
      return null;
    }
  };

  // Handle token input and decoding
  const handleToken1Change = (value: string) => {
    setToken1(value);
    if (value.trim()) {
      const decoded = decodeToken(value);
      if (decoded) {
        setDecoded1(decoded);
        setError1(null);
      } else {
        setDecoded1(null);
        setError1('Invalid JWT token format');
      }
    } else {
      setDecoded1(null);
      setError1(null);
    }
  };

  const handleToken2Change = (value: string) => {
    setToken2(value);
    if (value.trim()) {
      const decoded = decodeToken(value);
      if (decoded) {
        setDecoded2(decoded);
        setError2(null);
      } else {
        setDecoded2(null);
        setError2('Invalid JWT token format');
      }
    } else {
      setDecoded2(null);
      setError2(null);
    }
  };

  // Generate comparison insights
  const generateInsights = () => {
    if (!decoded1 || !decoded2) return;

    const newInsights: ComparisonInsight[] = [];

    // Token Type Comparison (ID vs Access)
    const aud1 = decoded1.payload.aud;
    const aud2 = decoded2.payload.aud;
    const scp1 = decoded1.payload.scp || decoded1.payload.scope;
    const scp2 = decoded2.payload.scp || decoded2.payload.scope;

    if (aud1 && aud2 && aud1 !== aud2) {
      newInsights.push({
        type: 'info',
        category: 'Audience',
        message: 'Different audiences detected',
        details: `Token 1 targets "${aud1}" while Token 2 targets "${aud2}". This is expected when comparing ID tokens (client ID) with access tokens (API/resource ID).`,
      });
    }

    // Version Comparison (v1 vs v2)
    const ver1 = decoded1.payload.ver;
    const ver2 = decoded2.payload.ver;

    if (ver1 && ver2 && ver1 !== ver2) {
      newInsights.push({
        type: 'warning',
        category: 'Token Version',
        message: `Comparing v${ver1} token with v${ver2} token`,
        details:
          'v1.0 and v2.0 tokens have different claim structures. v2.0 tokens use normalized claim names and may have different permission representations.',
      });
    }

    // Scope/Permission Comparison
    if (scp1 && scp2) {
      const scopes1 = scp1.split(' ').filter(Boolean);
      const scopes2 = scp2.split(' ').filter(Boolean);

      const onlyIn1 = scopes1.filter((s: string) => !scopes2.includes(s));
      const onlyIn2 = scopes2.filter((s: string) => !scopes1.includes(s));

      if (onlyIn1.length > 0) {
        newInsights.push({
          type: 'info',
          category: 'Scopes',
          message: `Token 1 has ${onlyIn1.length} unique scope(s)`,
          details: `Scopes only in Token 1: ${onlyIn1.join(', ')}`,
        });
      }

      if (onlyIn2.length > 0) {
        newInsights.push({
          type: 'info',
          category: 'Scopes',
          message: `Token 2 has ${onlyIn2.length} unique scope(s)`,
          details: `Scopes only in Token 2: ${onlyIn2.join(', ')}`,
        });
      }
    } else if (scp1 && !scp2) {
      newInsights.push({
        type: 'warning',
        category: 'Scopes',
        message: 'Token 1 has scopes, Token 2 does not',
        details: 'Token 2 may be an ID token (which typically has no scp claim) or a token with application permissions (roles claim).',
      });
    } else if (!scp1 && scp2) {
      newInsights.push({
        type: 'warning',
        category: 'Scopes',
        message: 'Token 2 has scopes, Token 1 does not',
        details: 'Token 1 may be an ID token (which typically has no scp claim) or a token with application permissions (roles claim).',
      });
    }

    // Application Roles Comparison
    const roles1 = decoded1.payload.roles;
    const roles2 = decoded2.payload.roles;

    if (roles1 && roles2) {
      const onlyIn1 = roles1.filter((r: string) => !roles2.includes(r));
      const onlyIn2 = roles2.filter((r: string) => !roles1.includes(r));

      if (onlyIn1.length > 0 || onlyIn2.length > 0) {
        newInsights.push({
          type: 'info',
          category: 'App Roles',
          message: 'Different app roles detected',
          details: `Token 1 unique roles: ${onlyIn1.join(', ') || 'none'}. Token 2 unique roles: ${onlyIn2.join(', ') || 'none'}.`,
        });
      }
    }

    // Issuer Comparison
    const iss1 = decoded1.payload.iss;
    const iss2 = decoded2.payload.iss;

    if (iss1 && iss2 && iss1 !== iss2) {
      // Check if difference is due to v1 vs v2 endpoint (same tenant)
      const isV1toV2Difference = 
        (iss1.includes('login.microsoftonline.com') && iss2.includes('sts.windows.net')) ||
        (iss2.includes('login.microsoftonline.com') && iss1.includes('sts.windows.net'));
      
      // Extract tenant IDs to check if same tenant
      const tenant1Match = iss1.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
      const tenant2Match = iss2.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
      const sameTenant = tenant1Match && tenant2Match && tenant1Match[0] === tenant2Match[0];

      if (isV1toV2Difference && sameTenant) {
        newInsights.push({
          type: 'info',
          category: 'Issuer',
          message: 'Different token endpoint versions detected (same tenant)',
          details: `Token 1: ${iss1}. Token 2: ${iss2}. v2.0 tokens use "login.microsoftonline.com" while v1.0 tokens use "sts.windows.net". Both are from Entra ID in the same tenant.`,
        });
      } else {
        newInsights.push({
          type: 'warning',
          category: 'Issuer',
          message: 'Different issuers detected',
          details: `Token 1: ${iss1}. Token 2: ${iss2}. ${sameTenant ? 'This indicates different endpoint versions or token types.' : 'This may indicate tokens from different tenants.'}`,
        });
      }
    }

    // Expiration Comparison
    const exp1 = decoded1.payload.exp;
    const exp2 = decoded2.payload.exp;

    if (exp1 && exp2) {
      const now = Math.floor(Date.now() / 1000);
      const expired1 = exp1 < now;
      const expired2 = exp2 < now;

      if (expired1 && !expired2) {
        newInsights.push({
          type: 'error',
          category: 'Expiration',
          message: 'Token 1 is expired',
          details: `Token 1 expired at ${new Date(exp1 * 1000).toLocaleString()}.`,
        });
      }

      if (!expired1 && expired2) {
        newInsights.push({
          type: 'error',
          category: 'Expiration',
          message: 'Token 2 is expired',
          details: `Token 2 expired at ${new Date(exp2 * 1000).toLocaleString()}.`,
        });
      }

      if (expired1 && expired2) {
        newInsights.push({
          type: 'error',
          category: 'Expiration',
          message: 'Both tokens are expired',
          details: 'Both tokens have expired and should not be used.',
        });
      }
    }

    // Token ID/Type hints
    if (!scp1 && !roles1 && aud1) {
      newInsights.push({
        type: 'info',
        category: 'Token Type',
        message: 'Token 1 appears to be an ID token',
        details: 'ID tokens contain user identity claims and are intended for the client application.',
      });
    }

    if (!scp2 && !roles2 && aud2) {
      newInsights.push({
        type: 'info',
        category: 'Token Type',
        message: 'Token 2 appears to be an ID token',
        details: 'ID tokens contain user identity claims and are intended for the client application.',
      });
    }

    setInsights(newInsights);
  };

  // Copy to clipboard
  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Get all unique claims
  const getAllClaims = (): string[] => {
    if (!decoded1 && !decoded2) return [];
    const claims = new Set<string>();
    if (decoded1) Object.keys(decoded1.payload).forEach((key) => claims.add(key));
    if (decoded2) Object.keys(decoded2.payload).forEach((key) => claims.add(key));
    return Array.from(claims).sort();
  };

  // Format claim value for display
  const formatClaimValue = (value: any): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    if (typeof value === 'number' && (value > 1000000000 && value < 2000000000)) {
      // Likely a timestamp
      return `${value} (${new Date(value * 1000).toLocaleString()})`;
    }
    return String(value);
  };

  // Get claim value color based on comparison
  const getClaimDiffStyle = (claim: string) => {
    if (!decoded1 || !decoded2) return {};

    const val1 = decoded1.payload[claim];
    const val2 = decoded2.payload[claim];

    if (val1 === undefined && val2 === undefined) return {};
    if (val1 === undefined) return { backgroundColor: '#fff3e0' }; // Only in token 2
    if (val2 === undefined) return { backgroundColor: '#e3f2fd' }; // Only in token 1
    if (JSON.stringify(val1) !== JSON.stringify(val2)) return { backgroundColor: '#fff9c4' }; // Different values

    return {};
  };

  // Trigger insights when both tokens are decoded
  React.useEffect(() => {
    if (decoded1 && decoded2) {
      generateInsights();
    } else {
      setInsights([]);
    }
  }, [decoded1, decoded2]);

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
      {/* Header Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f4f8fb',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              mr: 2,
              borderRadius: 2,
              pl: 1.5,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
                borderColor: "primary.main",
                color: "primary.main"
              }
            }}
          >
            Back
          </Button>
          
          <Box
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 0.5,
              flex: 1
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.01em',
              }}
            >
              Token Comparison Tool
            </Typography>
            
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              }}
            >
              Compare two JWT tokens side-by-side to understand differences (ID vs Access, v1 vs v2, etc.)
            </Typography>
          </Box>

          {decoded1 && decoded2 && (
            <ToggleButtonGroup
              value={comparisonMode}
              exclusive
              onChange={(_, value) => value && setComparisonMode(value)}
              size="small"
            >
              <ToggleButton value="side-by-side">
                <CompareArrowsIcon sx={{ mr: 1 }} fontSize="small" />
                Side by Side
              </ToggleButton>
              <ToggleButton value="diff">
                <SwapHorizIcon sx={{ mr: 1 }} fontSize="small" />
                Diff Only
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
      </Card>

      {/* Instructions */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>How to use:</strong> Paste two JWT tokens below to compare their claims, audiences, scopes, and
          more. Useful for understanding differences between ID tokens vs access tokens, v1.0 vs v2.0 tokens, or tokens
          from different resources.
        </Typography>
      </Alert>

      {/* Token Input Section */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper 
            sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%)',
              border: '1px solid',
              borderColor: 'primary.light',
              borderRadius: 2
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
              Token 1
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Paste JWT token here..."
              value={token1}
              onChange={(e) => handleToken1Change(e.target.value)}
              error={!!error1}
              helperText={error1}
              sx={{ 
                mb: 2, 
                fontFamily: 'monospace', 
                fontSize: '0.875rem',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 2
                }
              }}
            />
            {decoded1 && (
              <Box>
                <Chip
                  label={`Version: ${decoded1.payload.ver || 'unknown'}`}
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                  color="primary"
                />
                <Chip
                  label={`Type: ${decoded1.header.typ || 'JWT'}`}
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                  color="default"
                />
                {decoded1.payload.exp && (
                  <Chip
                    label={
                      decoded1.payload.exp * 1000 > Date.now()
                        ? `Valid until ${new Date(decoded1.payload.exp * 1000).toLocaleString()}`
                        : 'Expired'
                    }
                    size="small"
                    color={decoded1.payload.exp * 1000 > Date.now() ? 'success' : 'error'}
                    sx={{ mb: 1 }}
                  />
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper 
            sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, #fff3e0 0%, #ffffff 100%)',
              border: '1px solid',
              borderColor: 'warning.light',
              borderRadius: 2
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, color: 'warning.dark', fontWeight: 600 }}>
              Token 2
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Paste JWT token here..."
              value={token2}
              onChange={(e) => handleToken2Change(e.target.value)}
              error={!!error2}
              helperText={error2}
              sx={{ 
                mb: 2, 
                fontFamily: 'monospace', 
                fontSize: '0.875rem',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 2
                }
              }}
            />
            {decoded2 && (
              <Box>
                <Chip
                  label={`Version: ${decoded2.payload.ver || 'unknown'}`}
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                  color="primary"
                />
                <Chip
                  label={`Type: ${decoded2.header.typ || 'JWT'}`}
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                  color="default"
                />
                {decoded2.payload.exp && (
                  <Chip
                    label={
                      decoded2.payload.exp * 1000 > Date.now()
                        ? `Valid until ${new Date(decoded2.payload.exp * 1000).toLocaleString()}`
                        : 'Expired'
                    }
                    size="small"
                    color={decoded2.payload.exp * 1000 > Date.now() ? 'success' : 'error'}
                    sx={{ mb: 1 }}
                  />
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Insights Section */}
      {insights.length > 0 && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Comparison Insights
          </Typography>
          <Grid container spacing={2}>
            {insights.map((insight, index) => (
              <Grid item xs={12} key={index}>
                <Alert
                  severity={insight.type}
                  icon={
                    insight.type === 'success' ? (
                      <CheckCircleIcon />
                    ) : insight.type === 'warning' ? (
                      <WarningIcon />
                    ) : (
                      <InfoIcon />
                    )
                  }
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {insight.category}: {insight.message}
                  </Typography>
                  {insight.details && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                      {insight.details}
                    </Typography>
                  )}
                </Alert>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Comparison Table */}
      {decoded1 && decoded2 && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Claim-by-Claim Comparison
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: '20%' }}>Claim</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '40%' }}>Token 1</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '40%' }}>Token 2</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getAllClaims().map((claim) => {
                  const val1 = decoded1.payload[claim];
                  const val2 = decoded2.payload[claim];
                  const isDifferent = JSON.stringify(val1) !== JSON.stringify(val2);

                  return (
                    <TableRow key={claim} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      <TableCell sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                        {claim}
                        {isDifferent && (
                          <Chip label="DIFF" size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          ...getClaimDiffStyle(claim),
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                          <Box sx={{ flex: 1 }}>{formatClaimValue(val1)}</Box>
                          {val1 !== undefined && (
                            <Tooltip title={copiedField === `1-${claim}` ? 'Copied!' : 'Copy'}>
                              <IconButton size="small" onClick={() => handleCopy(formatClaimValue(val1), `1-${claim}`)}>
                                {copiedField === `1-${claim}` ? (
                                  <CheckCircleIcon fontSize="small" color="success" />
                                ) : (
                                  <ContentCopyIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          ...getClaimDiffStyle(claim),
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                          <Box sx={{ flex: 1 }}>{formatClaimValue(val2)}</Box>
                          {val2 !== undefined && (
                            <Tooltip title={copiedField === `2-${claim}` ? 'Copied!' : 'Copy'}>
                              <IconButton size="small" onClick={() => handleCopy(formatClaimValue(val2), `2-${claim}`)}>
                                {copiedField === `2-${claim}` ? (
                                  <CheckCircleIcon fontSize="small" color="success" />
                                ) : (
                                  <ContentCopyIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Legend */}
          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip label="Only in Token 1" size="small" sx={{ backgroundColor: '#e3f2fd' }} />
            <Chip label="Only in Token 2" size="small" sx={{ backgroundColor: '#fff3e0' }} />
            <Chip label="Different Values" size="small" sx={{ backgroundColor: '#fff9c4' }} />
          </Box>
        </Paper>
      )}

      {/* Common Use Cases */}
      <Paper 
        sx={{ 
          p: 3, 
          mt: 3, 
          background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Common Comparison Scenarios
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">ID Token vs Access Token</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  <strong>ID Token:</strong>
                </Typography>
                <Typography variant="caption" component="div" sx={{ mb: 2 }}>
                  • <code>aud</code>: Your application's client ID
                  <br />
                  • No <code>scp</code> claim (intended for client, not API)
                  <br />
                  • Rich user profile claims (<code>name</code>, <code>email</code>, etc.)
                  <br />• Purpose: Prove user identity to the client app
                </Typography>

                <Typography variant="body2" paragraph>
                  <strong>Access Token:</strong>
                </Typography>
                <Typography variant="caption" component="div">
                  • <code>aud</code>: Target API/resource ID (e.g., api://your-api)
                  <br />
                  • <code>scp</code> claim with delegated permissions
                  <br />
                  • Minimal user claims (only what API needs)
                  <br />• Purpose: Authorize API calls on behalf of user
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12} md={6}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">v1.0 Token vs v2.0 Token</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  <strong>v1.0 Token:</strong>
                </Typography>
                <Typography variant="caption" component="div" sx={{ mb: 2 }}>
                  • <code>ver: "1.0"</code>
                  <br />
                  • Uses <code>appidacr</code>, <code>appid</code> claims
                  <br />
                  • Issuer: <code>https://sts.windows.net/{'{'}{'{'}tenant{'}'}{'}'}/</code>
                  <br />• Endpoint: <code>/common</code>, <code>/organizations</code>
                </Typography>

                <Typography variant="body2" paragraph>
                  <strong>v2.0 Token:</strong>
                </Typography>
                <Typography variant="caption" component="div">
                  • <code>ver: "2.0"</code>
                  <br />
                  • Uses <code>azp</code>, <code>azpacr</code> claims
                  <br />
                  • Issuer: <code>https://login.microsoftonline.com/{'{'}{'{'}tenant{'}'}{'}'}/v2.0</code>
                  <br />• Normalized claim names, better OIDC compliance
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12} md={6}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Delegated vs Application Permissions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  <strong>Delegated (User Context):</strong>
                </Typography>
                <Typography variant="caption" component="div" sx={{ mb: 2 }}>
                  • <code>scp</code> claim contains space-separated scopes
                  <br />
                  • <code>oid</code>, <code>sub</code> claims identify the user
                  <br />
                  • Used in interactive flows (auth code, implicit, hybrid)
                  <br />• Example: <code>User.Read Mail.Send</code>
                </Typography>

                <Typography variant="body2" paragraph>
                  <strong>Application (App-Only):</strong>
                </Typography>
                <Typography variant="caption" component="div">
                  • <code>roles</code> claim contains app roles
                  <br />
                  • No user claims (<code>oid</code> refers to service principal)
                  <br />
                  • Used in client credentials flow
                  <br />• Example: <code>Application.ReadWrite.All</code>
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12} md={6}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Multi-Resource Tokens</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="caption" component="div">
                  When comparing tokens for different resources (e.g., Microsoft Graph vs your API):
                  <br />
                  <br />
                  • <code>aud</code> claim will differ (resource ID)
                  <br />
                  • <code>scp</code> claims will be resource-specific
                  <br />
                  • Same user (<code>oid</code>, <code>sub</code>) but different permissions
                  <br />
                  • Each API validates tokens meant for its audience
                  <br />
                  <br />
                  <strong>Key insight:</strong> You need separate tokens for each API/resource, even in the same app.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </Paper>

      {/* About Section - Expandable */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3, mt: 4 }}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                About Token Comparison Tool
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                This tool helps you understand the differences between two JWT tokens by comparing their claims side-by-side. 
                It's particularly useful for troubleshooting authentication issues, understanding why an API rejects a token, 
                or learning the differences between token types (ID vs Access) and versions (v1 vs v2).
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Common Use Cases:
                </Typography>
                <Box component="ul" sx={{ mt: 0.5, mb: 0.5, pl: 3 }}>
                  <li><Typography variant="body2" color="text.secondary"><strong>ID Token vs Access Token:</strong> Compare what claims are in each type and understand their different purposes</Typography></li>
                  <li><Typography variant="body2" color="text.secondary"><strong>v1 vs v2 Tokens:</strong> Identify endpoint version differences and claim format changes</Typography></li>
                  <li><Typography variant="body2" color="text.secondary"><strong>Different Scopes:</strong> See how permission changes affect the token's scp or roles claims</Typography></li>
                  <li><Typography variant="body2" color="text.secondary"><strong>Multi-Resource Tokens:</strong> Compare tokens for different APIs (e.g., Microsoft Graph vs custom API)</Typography></li>
                  <li><Typography variant="body2" color="text.secondary"><strong>Troubleshooting Rejections:</strong> Find missing or incorrect claims when an API rejects your token</Typography></li>
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  How to Use This Tool:
                </Typography>
                <Box component="ol" sx={{ pl: 3, '& li': { mb: 1 } }}>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Paste two JWT tokens</strong> into Token 1 and Token 2 input fields (tokens typically start with "eyJ")
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Review Smart Insights</strong> at the top for quick analysis of key differences (audience, scopes, version, expiration)
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Toggle between view modes:</strong> Side-by-side shows all claims from both tokens; Diff Only highlights differences
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Check the Claim-by-Claim table</strong> where unique claims are color-coded (blue for Token 1 only, orange for Token 2 only, yellow for different values)
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Click copy icons</strong> to copy individual claim values to your clipboard
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Explore educational sections</strong> below for guidance on common token comparison scenarios
                    </Typography>
                  </li>
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Understanding the Insights:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  The Smart Insights section automatically analyzes your tokens and highlights:
                </Typography>
                <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.5 } }}>
                  <li><Typography variant="body2" color="text.secondary"><strong>Audience differences:</strong> Shows if tokens are meant for different APIs or applications</Typography></li>
                  <li><Typography variant="body2" color="text.secondary"><strong>Token version:</strong> Identifies v1 vs v2 endpoint differences (sts.windows.net vs login.microsoftonline.com)</Typography></li>
                  <li><Typography variant="body2" color="text.secondary"><strong>Issuer comparison:</strong> Detects same-tenant endpoint differences vs different tenant tokens</Typography></li>
                  <li><Typography variant="body2" color="text.secondary"><strong>Scope differences:</strong> Highlights which permissions differ between tokens</Typography></li>
                  <li><Typography variant="body2" color="text.secondary"><strong>Role differences:</strong> Shows application role variations</Typography></li>
                  <li><Typography variant="body2" color="text.secondary"><strong>Expiration status:</strong> Warns if either token is expired or expiring soon</Typography></li>
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Color-Coded Comparison:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: 'rgba(33, 150, 243, 0.1)', borderColor: 'rgba(33, 150, 243, 0.3)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 0.5 }}>Blue Highlight</Typography>
                      <Typography variant="caption" color="text.secondary">Claim exists only in Token 1</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: 'rgba(255, 152, 0, 0.1)', borderColor: 'rgba(255, 152, 0, 0.3)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#f57c00', mb: 0.5 }}>Orange Highlight</Typography>
                      <Typography variant="caption" color="text.secondary">Claim exists only in Token 2</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: 'rgba(255, 235, 59, 0.2)', borderColor: 'rgba(255, 235, 59, 0.4)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#f57f17', mb: 0.5 }}>Yellow Highlight</Typography>
                      <Typography variant="caption" color="text.secondary">Claim exists in both but values differ</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'error.main' }}>
                  Security & Privacy:
                </Typography>
                <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.5 } }}>
                  <li><Typography variant="body2" color="text.secondary">All token decoding happens entirely in your browser - tokens are never sent to any server</Typography></li>
                  <li><Typography variant="body2" color="text.secondary">Be cautious when pasting production tokens with sensitive data</Typography></li>
                  <li><Typography variant="body2" color="text.secondary">Always verify token expiration and audience before accepting tokens in your application</Typography></li>
                  <li><Typography variant="body2" color="text.secondary">High-privilege claims (like directory roles) require extra scrutiny</Typography></li>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Token Comparison Tool vs JWT Decoder:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%', backgroundColor: 'rgba(227, 242, 253, 0.3)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                        Token Comparison Tool
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="div">
                        • Compare two tokens side-by-side<br />
                        • Identify claim differences<br />
                        • Smart insights for common scenarios<br />
                        • Best for troubleshooting why tokens behave differently
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%', backgroundColor: 'rgba(232, 245, 233, 0.3)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'success.main' }}>
                        JWT Token Decoder
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="div">
                        • Decode and inspect a single token<br />
                        • Detailed claim explanations<br />
                        • Security analysis and JWKS verification<br />
                        • Best for learning about individual token structure
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Quick tip:</Typography>
                  <Typography variant="body2">
                    Use Token Comparison when you have two tokens and need to understand their differences. 
                    Use JWT Decoder when you need deep analysis of a single token's claims and security properties.
                  </Typography>
                </Alert>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Card>
    </Box>
  );
};

export default TokenComparisonTool;
